/**
 * Tables A–F, transcribed from the engraved charts and grouped the way the book prints them.
 *
 * Every row is at written pitch where the book writes it that way, and says so. The rows where
 * the verifier accepts something the engraving does not show are called out rather than
 * silently widened — that difference is the whole argument the compass rule is having.
 */

import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import { CompassBar } from "../components/marks";
import Board from "../components/Board";
import { Failed, Kicker, Loading } from "../components/Page";
import { label } from "../lib/format";

export default function InstrumentsPage(): JSX.Element {
  const { data, error, loading } = useAsync("instruments", () => api.instruments());

  const shell = (children: JSX.Element): JSX.Element => (
    <Board kicker="Tables A – F" title="Compasses" measure="wide">
      {children}
    </Board>
  );
  if (loading) return shell(<Loading what="the compass tables" />);
  if (error !== null) return shell(<Failed error={error} />);
  if (data === null) return shell(<Failed error="the tables came back empty" />);

  const widened = data.tables
    .flatMap((table) => table.rows)
    .filter((row) => row.accepted.low !== row.chart.low || row.accepted.high !== row.chart.high);

  return (
    <Board
      kicker="Tables A – F"
      title="Compasses"
      lede={`${data.rowCount} rows, each transcribed from the engraved chart. The darker segment is the scope of greatest expression where the book marks one.`}
      measure="wide"
    >
      {/* Six charts and a voice table: columns, because a compass is read across its own row
          and nothing here is a sentence that wants a measure. */}
      <div className="grid items-start gap-6 lg:grid-cols-2">
        {data.tables.map((table) => (
          <section key={table.label} className="space-y-2">
            <Kicker>{table.label}</Kicker>
            <div className="overflow-hidden rounded-xl border border-rule bg-leaf">
              {table.rows.map((row) => (
                <div
                  key={row.instrument}
                  className="border-b border-rule-faint p-4 last:border-b-0"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-semibold capitalize">
                      {label(row.instrument)}
                    </span>
                    <span className="font-mono text-xs text-ink-quiet">
                      {row.chart.lowName} – {row.chart.highName}
                    </span>
                  </div>
                  <div className="mt-2.5">
                    <CompassBar row={row} />
                  </div>
                  <p className="mt-2 text-[11px] leading-relaxed text-ink-quiet">
                    {row.domain === "written" ? "written pitch" : `${row.domain} pitch`} ·{" "}
                    {row.provenance}
                    {row.approximate ? " · the book marks this row approximate" : ""}
                  </p>
                </div>
              ))}
            </div>
          </section>
        ))}

        <section className="space-y-2">
          <Kicker>Voices</Kicker>
          <div className="overflow-hidden rounded-xl border border-rule bg-leaf">
            {[...data.voices.chorus, ...data.voices.solo].map((row, index) => (
              <div
                key={`${row.voice}${index}`}
                className="flex items-baseline justify-between gap-3 border-b border-rule-faint px-4 py-3 last:border-b-0"
              >
                <span className="text-sm capitalize">{label(row.voice)}</span>
                <span className="font-mono text-xs text-ink-quiet">
                  {row.chart.lowName} – {row.chart.highName}
                </span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-ink-quiet">
            {data.voices.chorus.length} chorus ranges and {data.voices.solo.length} solo ranges. The
            book gives a chorus a narrower compass than the soloist singing the same part.
          </p>
        </section>

        {widened.length > 0 || data.approximate.length > 0 ? (
          <section className="rounded-xl border border-departure bg-departure/40 p-4 lg:col-span-2">
            <Kicker>Where the chart is not the answer</Kicker>
            <p className="mt-2 text-sm leading-relaxed text-departure-deep">
              {widened.length > 0
                ? `${widened.length} row${widened.length === 1 ? "" : "s"} accept notes the engraving does not show — ${widened
                    .map((row) => label(row.instrument))
                    .join(", ")}. `
                : ""}
              {data.approximate.length > 0
                ? `The ${data.approximate.map(label).join(", ")} row${
                    data.approximate.length === 1 ? " is" : "s are"
                  } marked approximate, and every fault reported against ${
                    data.approximate.length === 1 ? "it" : "them"
                  } says so.`
                : ""}
            </p>
          </section>
        ) : null}
      </div>
    </Board>
  );
}
